import React, { ReactNode, type FC, useState, useCallback } from "react";
import { Field } from "react-final-form";
import styles from "./FormField.module.scss";
import { AutofillFieldButton, DescriptionCollapseField, ErrorField, InputField, TitleField, MobileTooltip } from "~/components";
import { disabledValidate } from "~/utils/validate";
import { NamesOfFieldEnum, TypeInput } from "~/typings/types";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { TooltipPlacement } from "antd/es/tooltip";
import cn from "classnames";
import { AnimatedContainer } from "~/containers";

interface Props {
  name: string;
  shouldRenderTitleAboveField?: boolean;
  titleField?: string;
  mask?: string;
  typeField?: string;
  validate?: any;
  required?: boolean;
  tooltip?: string;
  isColon?: boolean;
  showCount?: boolean;
  styleContainerTitleField?: any;
  styleContainer?: any;
  placeholder?: string;
  typeInput?: TypeInput;
  maxLength?: number;
  children?: React.ReactNode;
  disabled?: boolean;
  addonBefore?: string;
  addonAfter?: string;
  renderError?: any;
  triggerAutofill?: string[];
  onBlurCallback?: any;
  typeRequestCheckPayformName?: any;
  setTypeRequestCheckPayformName?: any;
  errorMsgCheckPayformName?: string;
  placement?: TooltipPlacement;
  isFullWidthRow?: boolean;
  allowMultiline?: boolean;
  showError?: boolean;
  descriptionField?: string | React.ReactNode;
  classNameInput?: string;
  icon?: React.ReactNode;
}

const removeSpaces = (value: string) => value && value?.trim();
const antIcon = <LoadingOutlined className={styles.spin} spin />;

export const FormField: FC<Props> = ({
  shouldRenderTitleAboveField = false,
  typeField = "text",
  children,
  name,
  titleField,
  validate,
  required,
  tooltip,
  isColon,
  showCount,
  styleContainerTitleField,
  styleContainer,
  typeInput,
  maxLength,
  mask,
  disabled,
  addonBefore,
  addonAfter,
  renderError,
  triggerAutofill,
  onBlurCallback,
  typeRequestCheckPayformName,
  setTypeRequestCheckPayformName,
  errorMsgCheckPayformName,
  placement,
  allowMultiline,
  isFullWidthRow, // Работает только для customer v2 questionare. можно расположить как в 1 так и 2 столбца филды на одной строке
  showError = true,
  placeholder,
  descriptionField,
  classNameInput,
  icon,
}) => {
  const isLoadingCheckPayformName = typeRequestCheckPayformName === "loading";
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const configDescriptionField: any = {
    loading: {
      has: 1,
      message: "Проверка названия платежной страницы",
      color: "grey"
    },
    success: {
      has: 1,
      message: "Данное название платежной страницы свободно",
      color: "#437a54"
    },
    confirmedFullnameByInn: {
      has: 1,
      message: "ФИО подтверждено по ИНН",
      color: "#437a54"
    },
    error: {
      has: 1,
      message: "Данное название платежной страницы занято",
      color: "#E41D36"
    },
    errorValidateFromApi: {
      has: 1,
      message: errorMsgCheckPayformName,
      color: "#E41D36"
    }
  };

  return (
    <div className={cn(styles.containerField, {
      [styles.fullWidthRow]: isFullWidthRow
    })} style={styleContainer}>
      {titleField && shouldRenderTitleAboveField && (
        <TitleField
          name={titleField}
          required={required}
          tooltip={tooltip}
          isColon={isColon}
          styleContainer={styleContainerTitleField}
          placement={placement}
          typeField={typeField}
          icon={icon}
        />
      )}
      {children || (
        <Field
          name={name}
          component="input"
          type={typeField}
          validate={disabledValidate(validate)}
          formatOnBlur
          format={removeSpaces}
        >
          {({ input, meta }) => (
            <div className={styles.containerInput}>
              <InputField
                mask={mask}
                input={input}
                meta={meta}
                tooltip={tooltip}
                required={required}
                titleField={titleField}
                typeInput={typeInput}
                maxLength={maxLength}
                disabled={disabled}
                addonBefore={addonBefore}
                addonAfter={addonAfter}
                showCount={showCount}
                onBlurCallback={(e: React.FocusEvent<HTMLInputElement>) => {
                  if (input.name !== NamesOfFieldEnum.PayformName) {
                    setIsTooltipVisible(false);
                  }
                  onBlurCallback?.(e);
                }}
                allowMultiline={allowMultiline}
                typeRequestCheckPayformName={typeRequestCheckPayformName}
                setTypeRequestCheckPayformName={setTypeRequestCheckPayformName}
                onFocusCallback={(e: any) => {
                  setIsTooltipVisible(true);
                }}
                placeholder={placeholder}
                classNameInput={classNameInput}
              />
              {isLoadingCheckPayformName && (
                <Spin indicator={antIcon} className={styles.containerSpin} />
              )}
            </div>
          )}
        </Field>
      )}
      <AnimatedContainer isVisible={isTooltipVisible}>
        <MobileTooltip content={tooltip} />
        <AutofillFieldButton triggerAutofill={triggerAutofill} />
      </AnimatedContainer>
      {showError && <ErrorField
        name={name}
        renderError={renderError || configDescriptionField[typeRequestCheckPayformName]}
        descriptionField={descriptionField}
      />}
    </div>
  );
};
